import React, { Fragment } from 'react'
import Masonry from 'react-masonry-css'
import styled from 'styled-components'
import { useMedia } from 'react-use'

// config
import constants from 'config/constants'
import l from 'config/localization'

// utils
import th from 'utils/themeHelper'

// Load StoryCard synchronously
import ConditionalDisplay from 'components/ConditionalDisplay/ConditionalDisplay'
import LinkButton from 'components/SmallButtonPrimary/LinkButton'
import StoryCard from 'components/StoryCard/StoryCard'

// assets
import privateIcon from 'assets/images/svgs/icon-public-opp.svg'

const singleColumn = 1
const twoColumns = 2

const ShowMoreStoriesWrapper = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  width: 100%;
`

const PrivateStoryLabel = styled.div`
  display: inline;
  font-family: ${th('fonts.bold')};
  font-size: 14px;
`

const PrivateStoryLabelWrapper = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 10px;
`

const PrivateStoryIcon = styled.div`
  border-color: ${th('borders.medium')};
  background-color: white;
  background-image: url(${({ src }) => src});
  background-position: 50%;
  background-repeat: no-repeat;
  background-size: 65%;
  border-radius: 50%;
  border-style: solid;
  border-width: 1px;
  height: 30px;
  margin-right: 10px;
  padding: 5px;
  width: 30px;
`

const PrivateStoryNotificationCard = styled.div`
  background: ${th('transparent.base')};
  border-color: ${th('borders.medium')};
  border-radius: 5px;
  border-style: solid;
  border-width: 1px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  line-height: 1.35;
  margin-bottom: 20px;
  overflow: hidden;
  position: relative;
  padding: 10px;

  &:hover {
    box-shadow: ${props =>
      props.hoverShadow ? '0 0 4px #379b75' : '0 0 10px 0 rgba(0, 0, 0, 0.15)'};
  }

  &:hover .card-progress-bar-is-zero-hover {
    color: white;
    background: ${th('primary.base')};
  }
`

const deriveBreakpointCols = ({ columns, stories, isWide }) => {
  // Mobile so single column
  if (!isWide) return singleColumn

  // Derive columns but give precedence to props
  // By default, 1 column on mobile and then 2 columns on wider screens
  const hasColumnsPropButSingleStory = columns && stories.length === 1

  return hasColumnsPropButSingleStory ? singleColumn : columns || twoColumns
}

const shouldDisplayImages = ({ story, masonryClassName }) => {
  if (masonryClassName !== 'opp-masonry') return true

  const isUpdateOrFeatureStory = [
    constants.STORY_TYPES.OPP_FEATURE,
    constants.STORY_TYPES.OPP_UPDATE
  ].includes(story.story_type)

  return isUpdateOrFeatureStory
}

const StoryFeed = props => {
  const {
    categories = [],
    columns,
    dark,
    displayShowMoreButton,
    handleStoryLike,
    hasGodRole,
    hasPrivateActivity,
    masonryClassName,
    showAllStories,
    stories = [],
    totalStoryCount = 0,
    updateStory
  } = props

  if (!masonryClassName) {
    return <div>Please specify a `masonryClassName`</div>
  }

  const isWide = useMedia('(min-width: 769px)')
  const breakpointCols = deriveBreakpointCols({ columns, stories, isWide })
  const className = masonryClassName || 'my-masonry-grid'
  const isUserProfile = masonryClassName === 'user-masonry'

  const hasTwoCols = breakpointCols === 2
  const hasFourCols = breakpointCols === 4
  const columnClassName = hasTwoCols
    ? `${className}_column two_col_masonry_column`
    : hasFourCols
    ? `${className}_column four_col_masonry_column`
    : `${className}_column`

  const isOppMasonry = masonryClassName === 'opp-masonry'
  const isOrgMasonry = masonryClassName === 'org-masonry'

  return (
    <Fragment>
      <Masonry
        breakpointCols={breakpointCols}
        className={className}
        columnClassName={columnClassName}
      >
        <ConditionalDisplay displayWhen={[hasPrivateActivity]}>
          <PrivateStoryNotificationCard>
            <PrivateStoryLabelWrapper>
              <PrivateStoryIcon src={privateIcon} />

              <PrivateStoryLabel>Your activity</PrivateStoryLabel>
            </PrivateStoryLabelWrapper>

            <div>
              You have joined this {l('OPP')} but have opted for your activity to remain private.
              Nobody else can see your activity on this page. Your activity is visible on your story
              feed on your profile page, but only to you.
            </div>
          </PrivateStoryNotificationCard>
        </ConditionalDisplay>

        {stories.map((story, idx) => {
          const showCardImages = shouldDisplayImages({ story, masonryClassName })

          return (
            <StoryCard
              categories={categories}
              dark={dark}
              key={`story_feed_story_card_${idx}`}
              handleStoryLike={handleStoryLike}
              hasGodRole={hasGodRole}
              hideCardImages={!showCardImages}
              isOppMasonry={isOppMasonry}
              isOrgMasonry={isOrgMasonry}
              isUserProfile={isUserProfile}
              updateStory={updateStory}
              {...story}
            />
          )
        })}
      </Masonry>

      <ConditionalDisplay
        displayWhen={[displayShowMoreButton, totalStoryCount > constants.STORY_FEED_DEFAULT_LENGTH]}
      >
        <ShowMoreStoriesWrapper>
          <LinkButton onClick={showAllStories}>Show More Stories</LinkButton>
        </ShowMoreStoriesWrapper>
      </ConditionalDisplay>
    </Fragment>
  )
}

export default StoryFeed
